import type { FormikProps } from 'formik';
import { type ChangeEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { object, string } from 'yup';
import { Spinner, StyledButton, StyledSelect, StyledTextField, StyledTypeahead } from '../../components';
import { useTSPProviders, getAgreementsForUser, getId } from '../../services';
import { canadaProvinces, countries, mexicoStates, usStates } from '../../text';
import type { INewAgreementFields, INewAgreementValues } from '../../types';
import { everyPairNonEmpty, isValidPhoneNumberFormat, isValidPostalCodeFormat } from '../../utils';

export const schema = object({
	tsp: string().required(`Telematic Solution is required`),
	fleetIdentifier: string().when('tsp', ([isTsp]) => {
		return isTsp
			? string().required(`Geotab Database Name is required`)
			: string().required(`Fleet Identifier is required`);
	}),
	name: string().required('Company name is required'),
	address: string().required('Address is required'),
	city: string().required('City is required'),
	state: string().required('State or Province is required'),
	country: string().required('Country is required'),
	zip: string().test('len', 'Must match US, Mexico, or Canada postal code.', value => isValidPostalCodeFormat(value)),
	phone: string()
		.test('format', 'Must be valid US, Mexico, or Canada phone number. Ex. 123-456-7890', value =>
			isValidPhoneNumberFormat(value)
		)
		.required('Phone number is required'),
	representativeName: string().required('Name is required'),
	representativeTitle: string().required('Title is required'),
	representativePhone: string()
		.test('format', 'Must be valid US, Mexico, or Canada phone number. Ex. 123-456-7890', value =>
			isValidPhoneNumberFormat(value)
		)
		.required('Phone number is required'),
	representativeEmail: string().email('Enter a valid email').required('Email is required'),
});

const Form = styled('form')`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	width: 100%;
`;

const FormGrid = styled('div')`
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-template-rows: repeat(7, auto);
	column-gap: 2rem;
	width: 100%
`;

const FormFooter = styled('div')`
	display: flex;
	justify-content: space-around;
	width: 100%;
	flex-grow: 1;
`;

interface IAgreementFormProps {
	onSave?: (v: INewAgreementValues) => void;
}

export const AgreementForm = (props: FormikProps<INewAgreementValues> & IAgreementFormProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { values, errors, touched, handleChange, setFieldTouched, setFieldValue, validateForm, dirty } = props;
    const [userId, setUserId] = useState<number | null>(null);
	const [isIdComplete, setIsIdComplete] = useState(false);
	const { tspProviders } = useTSPProviders();

	const fields: INewAgreementFields = {
		tsp: { name: 'tsp', label: 'Telematic Solution' },
		fleetIdentifier: { name: 'fleetIdentifier', label: 'Telematic Solution Fleet Identifier' },
		name: { name: 'name', label: 'Company Name' },
		address: { name: 'address', label: 'Company Address' },
		city: { name: 'city', label: 'Company City' },
		state: { name: 'state', label: 'Company State or Province' },
		country: { name: 'country', label: 'Company Country' },
		zip: { name: 'zip', label: 'Company Zip Code' },
		phone: { name: 'phone', label: 'Company Phone Number' },
		representativeName: { name: 'representativeName', label: 'Fleet Representative Name' },
		representativeTitle: { name: 'representativeTitle', label: 'Fleet Representative Title' },
		representativeEmail: { name: 'representativeEmail', label: 'Fleet Representative Email' },
		representativePhone: { name: 'representativePhone', label: 'Fleet Representative Phone Number' },
	};

	const [isDisabled, setIsDisabled] = useState(true);
	const [submittable, setSubmittable] = useState(false);
	const [statesOrProvinces, setStatesOrProvinces] = useState([{ index: '1', value: '1', label: '' }]);

    useEffect(() => {
		const r = async () =>
			getId().then(
				res => {
					setUserId(res.data);
					setIsIdComplete(true);
				},
				() => setIsIdComplete(true)
			);
		r();
	}, []);

	useEffect(() => {
		if (!isIdComplete || userId === null)
			return;

		const result = async () => {
			getAgreementsForUser(userId.toString()).then((res: any) => setIsDisabled(res.data.resultCount === 0));
		};
		result();
	}, [isIdComplete, userId]);

	useEffect(() => {
		if (location.pathname === '/agreement/update' && !dirty) {
			validateForm(values);
			Object.keys(errors).map(e => setFieldTouched(e, true));
		}
		setSubmittable(everyPairNonEmpty(values, Object.keys(errors).length > 0));
	}, [values, errors, dirty, location.pathname, validateForm, setFieldTouched]);

	const change = (name: any, e: ChangeEvent) => {
		handleChange(e);
		setFieldTouched(name, true, false);
	};

	return (
		<>
            {tspProviders.isComplete && isIdComplete ? (
				<Form onSubmit={props.handleSubmit}>
					<FormGrid>
						<StyledTextField
							style={{ gridColumn: '1', gridRow: '1' }}
							disabled={isDisabled}
							value={values.name || ''}
							name={fields.name.name}
							label={fields.name.label}
							helperText={touched.name ? errors.name : ''}
							error={touched.name && errors.name !== undefined}
							onChange={(e: ChangeEvent) => (isDisabled ? '' : change(fields.name.name, e))}
						/>
						<StyledSelect
							style={{ gridColumn: '1', gridRow: '2' }}
							value={values.tsp || ''}
							name={fields.tsp.name}
							label={fields.tsp.label}
							menuItems={tspProviders.data}
							onChange={(e: ChangeEvent) => change(fields.tsp.name, e)}
						/>
						<StyledTextField
							style={{ gridColumn: '2', gridRow: '2' }}
							autoFocus
							value={values.fleetIdentifier}
							name={fields.fleetIdentifier.name}
							label={values.tsp !== 4 ? fields.fleetIdentifier.label : 'Geotab Database Name'}
							helperText={touched.fleetIdentifier ? errors.fleetIdentifier : ''}
							error={touched.fleetIdentifier && errors.fleetIdentifier !== undefined}
							onChange={(e: ChangeEvent) => change(fields.fleetIdentifier.name, e)}
						/>
						<StyledTextField
							style={{ gridColumn: '1', gridRow: '3' }}
							autoFocus={isDisabled}
							value={values.address}
							name={fields.address.name}
							label={fields.address.label}
							helperText={touched.address ? errors.address : ''}
							error={touched.address && errors.address !== undefined}
							onChange={(e: ChangeEvent) => change(fields.address.name, e)}
						/>
						<StyledTextField
							style={{ gridColumn: '2', gridRow: '3' }}
							value={values.city}
							name={fields.city.name}
							label={fields.city.label}
							helperText={touched.city ? errors.city : ''}
							error={touched.city && errors.city !== undefined}
							onChange={(e: ChangeEvent) => change(fields.city.name, e)}
						/>
						<StyledTypeahead
							style={{ gridColumn: '1', gridRow: '4' }}
							options={countries}
							value={values.country}
							name={fields.country.name}
							label={fields.country.label}
							placeholder="Choose Country..."
							error={touched.country && errors.country !== undefined}
							onBlur={setFieldTouched}
							onChange={(name: string, value: any) => {
								setFieldValue(name, value);
								setFieldValue(fields.state.name, '0');
								const currentStates =
									value === '1' ? usStates : value === '2' ? canadaProvinces : mexicoStates;
								setStatesOrProvinces(currentStates);
							}}
						/>
						<StyledTypeahead
							style={{ gridColumn: '2', gridRow: '4' }}
							options={statesOrProvinces}
							value={values.state}
							name={fields.state.name}
							label={fields.state.label}
							placeholder="Choose State..."
							helperText={touched.zip ? errors.zip : ''}
							error={touched.state && errors.state !== undefined}
							onBlur={setFieldTouched}
							onChange={setFieldValue}
						/>
						<StyledTextField
							style={{ gridColumn: '1', gridRow: '5' }}
							disabled={isDisabled}
							value={values.zip || ''}
							name={fields.zip.name}
							label={fields.zip.label}
							helperText={touched.zip ? errors.zip : ''}
							error={touched.zip && errors.zip !== undefined}
							onChange={(e: ChangeEvent) => (isDisabled ? '' : change(fields.zip.name, e))}
						/>
						<StyledTextField
							style={{ gridColumn: '2', gridRow: '5' }}
							value={values.phone}
							name={fields.phone.name}
							label={fields.phone.label}
							helperText={touched.phone ? errors.phone : ''}
							error={touched.phone && errors.phone !== undefined}
							onChange={(e: ChangeEvent) => change(fields.phone.name, e)}
						/>
						<StyledTextField
							style={{ gridColumn: '1', gridRow: '6' }}
							value={values.representativeName}
							name={fields.representativeName.name}
							label={fields.representativeName.label}
							helperText={touched.representativeName ? errors.representativeName : ''}
							error={touched.representativeName && errors.representativeName !== undefined}
							onChange={(e: ChangeEvent) => change(fields.representativeName.name, e)}
						/>
						<StyledTextField
							style={{ gridColumn: '2', gridRow: '6' }}
							value={values.representativeTitle}
							name={fields.representativeTitle.name}
							label={fields.representativeTitle.label}
							helperText={touched.representativeTitle ? errors.representativeTitle : ''}
							error={touched.representativeTitle && errors.representativeTitle !== undefined}
							onChange={(e: ChangeEvent) => change(fields.representativeTitle.name, e)}
						/>
						<StyledTextField
							style={{ gridColumn: '1', gridRow: '7' }}
							value={values.representativeEmail || ''}
							name={fields.representativeEmail.name}
							label={fields.representativeEmail.label}
							helperText={touched.representativeEmail ? errors.representativeEmail : ''}
							error={touched.representativeEmail && errors.representativeEmail !== undefined}
							onChange={(e: ChangeEvent) => change(fields.representativeEmail.name, e)}
						/>
						<StyledTextField
							style={{ gridColumn: '2', gridRow: '7' }}
							value={values.representativePhone}
							name={fields.representativePhone.name}
							label={fields.representativePhone.label}
							helperText={touched.representativePhone ? errors.representativePhone : ''}
							error={touched.representativePhone && errors.representativePhone !== undefined}
							onChange={(e: ChangeEvent) => change(fields.representativePhone.name, e)}
						/>
					</FormGrid>
					<FormFooter>
						<StyledButton onClick={() => navigate('/')}>CANCEL</StyledButton>
						<StyledButton onClick={() => props.onSave?.(values)} disabled={!values.tsp}>
							SAVE AND CONTINUE LATER
						</StyledButton>
						<StyledButton type="submit" disabled={!submittable}>
							CONTINUE
						</StyledButton>
					</FormFooter>
				</Form>
			) : (
				<Spinner />
			)}
		</>
	);
};
